import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  CanLoad,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  Route,
  UrlSegment
} from '@angular/router';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkRoleAccess(state.url, route.data);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkRoleAccess(state.url, childRoute.data);
  }

  canLoad(route: Route, segments: UrlSegment[]): boolean {
    const url = '/' + segments.map(segment => segment.path).join('/');
    return this.checkRoleAccess(url, route.data);
  }

  private checkRoleAccess(url: string, routeData: any): boolean {
    console.log('RoleGuard: Checking access for URL:', url);
    
    // First check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('RoleGuard: User not authenticated, redirecting to login');
      // Route to correct login based on attempted area
      if (url.startsWith('/company') || url.startsWith('/financial-advisor')) {
        this.router.navigate(['/login-service-provider']);
      } else {
        // Remember where the customer was going; Quick Login returns them there.
        this.router.navigate(['/login'], { queryParams: { returnUrl: url } });
      }
      return false;
    }

    const userRole = this.authService.getUserRole();
    console.log('RoleGuard: User role:', userRole);

    // If route specifies required roles, check them
    if (routeData && routeData['roles']) {
      const requiredRoles = routeData['roles'] as string[];
      console.log('RoleGuard: Required roles:', requiredRoles);
      
      if (!userRole || !requiredRoles.includes(userRole)) {
        console.log('RoleGuard: User does not have required role, redirecting');
        this.authService.redirectToDefaultRoute();
        return false;
      }
    }

    // Check if user can access the route based on URL pattern
    if (!this.authService.canAccessRoute(url)) {
      console.log('RoleGuard: User cannot access route based on role, redirecting');
      this.authService.redirectToDefaultRoute();
      return false;
    }

    // Additional role-specific checks
    if (!this.performRoleSpecificChecks(url, userRole)) {
      console.log('RoleGuard: Role-specific check failed, redirecting');
      this.authService.redirectToDefaultRoute();
      return false;
    }

    console.log('RoleGuard: Access granted for URL:', url);
    return true;
  }

  private performRoleSpecificChecks(url: string, userRole: string | null): boolean {
    if (!userRole) {
      return false;
    }

    // Customer-specific checks
    if (userRole === 'customer') {
      // Customers should not access company routes
      if (url.startsWith('/company')) {
        console.log('RoleGuard: Customer attempting to access company route');
        return false;
      }
      
      // Customers can access their own routes, logout, and shared routes
      const allowedPatterns = ['/customer', '/logout', '/pdf-view'];
      return allowedPatterns.some(pattern => url.startsWith(pattern));
    }

    // Company/Influencer-specific checks
    if (userRole === 'company' || userRole === 'influencer' || userRole === 'subaccount') {                                                                     
      // Companies should not access customer routes
      if (url.startsWith('/customer')) {
        console.log('RoleGuard: Company/Influencer attempting to access customer route');
        return false;
      }
      
      // Companies can access their own routes, logout, and shared routes
      const allowedPatterns = ['/company', '/logout', '/pdf-view'];
      return allowedPatterns.some(pattern => url.startsWith(pattern));
    }

    // Financial Advisor-specific checks
    if (userRole === 'financialadvisor') {
      if (url.startsWith('/customer') || url.startsWith('/company')) {
        return false;
      }
      const allowedPatterns = ['/financial-advisor', '/logout', '/pdf-view'];
      return allowedPatterns.some(pattern => url.startsWith(pattern));
    }

    // Unknown role
    console.log('RoleGuard: Unknown user role:', userRole);
    return false;
  }
} 